/**
 * "1 entry waiting for your card" (owner, 2026-10-10; services/api heldentries.ts). A business with no card yet keeps each
 * Saved record as the person's own draft. This line, in the layout under the banners, says how many of mine wait, with one
 * action: Add a card (owner or admin, on the web), or who adds it (staff), or nothing to tap in the phone app's browser (it
 * never asks for a card). Once the card is in, it saves them (each once, with the time it was entered) and says so.
 * Never on the sample café or a home (neither holds a record).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CARD_TO_SAVE } from "@logwave/core";
import { CARD_TO_SAVE_EVENT, HELD_CHANGED_EVENT, isHomeOrg, type HeldEntriesResponse } from "../api.ts";
import { isDemoSample } from "../lib/demoAsk.ts";
import { viaApp } from "../lib/storeApp.ts";
import { useApi, useSession } from "../session.tsx";
import { Icon } from "./Icons.tsx";
import { Button, useToast } from "./ui.tsx";

export function HeldEntries() {
  const s = useSession();
  return isHomeOrg(s.org) || isDemoSample(s.org) ? null : <HeldEntriesHere />;
}

function HeldEntriesHere() {
  const s = useSession();
  const api = useApi();
  const nav = useNavigate();
  const toast = useToast();
  const [data, setData] = useState<HeldEntriesResponse | null>(null);
  const busy = useRef(false);
  const orgId = s.org.id;
  const load = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    try {
      let r = await api.heldEntries(orgId);
      // The card is in: save mine now (the server sends each through its own route again, once).
      if (r.canSave && r.waiting > 0) {
        r = await api.saveHeld(orgId);
        if (r.saved?.length) toast.show(r.line ?? CARD_TO_SAVE.savedLine(r.saved.length), { icon: "check" });
      }
      setData(r);
    } catch { /* the line waits for the next try; nothing here blocks a screen */ }
    finally { busy.current = false; }
  }, [api, orgId, toast]);
  useEffect(() => {
    void load();
    const on = () => void load();
    try { for (const ev of [CARD_TO_SAVE_EVENT, HELD_CHANGED_EVENT, "focus"]) window.addEventListener(ev, on); } catch { return; }
    return () => { for (const ev of [CARD_TO_SAVE_EVENT, HELD_CHANGED_EVENT, "focus"]) window.removeEventListener(ev, on); };
  }, [load]);
  const retry = async () => { try { const r = await api.saveHeld(orgId, { retry: true }); if (r.saved?.length) toast.show(r.line ?? CARD_TO_SAVE.savedLine(r.saved.length), { icon: "check" }); setData(r); } catch { /* try again later */ } };
  const discard = async (id: string) => { try { const r = await api.discardHeld(orgId, id); setData((d) => (d ? { ...d, entries: r.entries, waiting: r.waiting } : d)); } catch { /* already gone */ } };
  if (!data || data.waiting === 0) return null;
  const canPay = s.can("admin");
  const failed = data.entries.find((e) => e.lastError);
  return (
    <div className="offline billing-banner billing-held" role="status" data-testid="held-line">
      <p><Icon name="clock" />{data.words?.line ?? CARD_TO_SAVE.waitingLine(data.waiting)}{" "}
        {canPay && !viaApp() && !data.canSave ? <Button small variant="secondary" onClick={() => nav("/billing/start")} data-testid="held-add-card">{CARD_TO_SAVE.addCard}</Button> : null}</p>
      <p className="small">{canPay ? "" : `${CARD_TO_SAVE.askOwner(s.ownerName ?? null)} `}{data.words?.kept ?? CARD_TO_SAVE.kept} {data.words?.expiry ?? CARD_TO_SAVE.expiry}</p>
      {failed && (
        <p className="small" data-testid="held-error">{failed.label}: {failed.lastError}{" "}
          {data.canSave && <Button small variant="secondary" onClick={() => void retry()} data-testid="held-retry">Try again</Button>}{" "}
          <Button small variant="ghost" onClick={() => void discard(failed.id)} data-testid="held-discard">Discard</Button>
        </p>
      )}
    </div>
  );
}
